/**
 * Simple | Pro door memory. `simple` = ELPM Creator (guided), `pro` = the
 * 64-channel Pro Studio. Remembered per browser; `/` routes on it.
 */
import { useCallback, useEffect, useState } from "react";

const MODE_KEY = "elpm-studio-mode";
const CURRENT_SONG_KEY = "elpm-creator-current-song";
const EVT = "elpm:studio-mode";

export function getStudioMode() {
  try { return localStorage.getItem(MODE_KEY) === "pro" ? "pro" : "simple"; } catch (e) { void e; return "simple"; }
}

export function setStudioMode(mode) {
  try { localStorage.setItem(MODE_KEY, mode === "pro" ? "pro" : "simple"); } catch (e) { void e; }
  if (typeof window !== "undefined") window.dispatchEvent(new Event(EVT));
}

export function useStudioMode() {
  const [mode, setModeState] = useState(getStudioMode);
  useEffect(() => {
    const sync = () => setModeState(getStudioMode());
    window.addEventListener(EVT, sync);
    window.addEventListener("storage", sync);
    return () => { window.removeEventListener(EVT, sync); window.removeEventListener("storage", sync); };
  }, []);
  const setMode = useCallback((m) => setStudioMode(m), []);
  return [mode, setMode];
}

/** The song the Creator is currently working on (library id) — survives the Simple↔Pro flip. */
export function getCurrentSongId() {
  try { return localStorage.getItem(CURRENT_SONG_KEY) || null; } catch (e) { void e; return null; }
}

export function setCurrentSongId(id) {
  try {
    if (id) localStorage.setItem(CURRENT_SONG_KEY, id);
    else localStorage.removeItem(CURRENT_SONG_KEY);
  } catch (e) { void e; }
}

/** Route the Pro Studio should open at, carrying the current Creator song when there is one. */
export function proStudioPath(songId = getCurrentSongId(), intent = null) {
  const params = new URLSearchParams();
  if (songId) params.set("song", songId);
  if (intent) params.set("intent", intent);
  const q = params.toString();
  return `/studio${q ? `?${q}` : ""}`;
}

/** Route the Creator should open at when flipping back to Simple. */
export function creatorPath(songId = getCurrentSongId()) {
  return songId ? `/create/song/${songId}` : "/create";
}
